import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useQuestions } from "@/lib/useInsights";
import { PrintBar, ResourceNotice } from "@/components/ResourceNotice";
import { PackPage, PackCover, SectionCover } from "@/components/pack/PackPage";
import { growPages, NotesPage } from "@/components/pack/GrowPages";
import { Topic, Intro } from "@/pages/SelfEvaluationWorkbook";
import { Question, ScoringTable, ScoringKeys } from "@/pages/DiyAssessment";
import { ColoringSheetContent } from "@/pages/ColoringSheet";

export function usePackPrint() {
  useEffect(() => {
    document.body.classList.add("pack-print");
    return () => document.body.classList.remove("pack-print");
  }, []);
}

const chunk = (arr, sizes) => {
  let i = 0;
  return sizes.map((s) => arr.slice(i, (i += s)));
};

const BackCover = () => (
  <div className="flex h-full flex-col justify-end" data-testid="pk-back-cover">
    <ResourceNotice showQr />
  </div>
);

export const PackDocument = ({ pages, testId }) => {
  const padded = [...pages];
  while ((padded.length + 1) % 4 !== 0) padded.push({ section: null, el: <NotesPage /> });
  padded.push({ section: null, el: <BackCover />, plain: true });
  const total = padded.length;
  return (
    <div className="pk-doc mx-auto flex flex-col items-center gap-8" data-testid={testId}>
      {padded.map((p, i) => (
        <PackPage key={i} n={i + 1} total={total} section={p.section} plain={p.plain}>{p.el}</PackPage>
      ))}
    </div>
  );
};

function usePackData() {
  const q = useQuestions();
  const [guide, setGuide] = useState(null);
  const [questions, setQuestions] = useState(null);
  useEffect(() => {
    api.get("/coaching-guide").then((r) => setGuide(r.data)).catch(() => setGuide([]));
    api.get("/scoring").then((r) => setQuestions(r.data.questions)).catch(() => setQuestions([]));
  }, []);
  return { guide, questions, desc: q?.descriptions || {}, ready: guide && questions && q };
}

export default function WorkshopPack() {
  usePackPrint();
  const { guide, questions, desc, ready } = usePackData();
  if (!ready) return <div className="py-20 text-center text-slate-500">Preparing workshop pack…</div>;

  const evalPages = [guide.slice(0, 1), ...chunk(guide.slice(1), Array(12).fill(2))];
  const diyChunks = chunk(questions, [6, 7, 6, 6]);
  const starts = [1, 7, 14, 20];
  const pages = [
    { section: null, el: <PackCover title="Assessment Workshop Pack" subtitle="Rate · Score · Color · Grow" />, plain: true },
    { section: "rate", el: <SectionCover id="rate" /> },
    ...evalPages.map((ts, p) => ({
      section: "rate",
      el: (
        <div className="pk-fit">
          {p === 0 && <Intro />}
          {ts.map((t) => <Topic key={t.key} t={t} n={guide.indexOf(t) + 1} />)}
        </div>
      ),
    })),
    { section: "score", el: <SectionCover id="score" /> },
    ...diyChunks.map((qs, p) => ({
      section: "score",
      el: (
        <div className="pk-fit">
          {p === 0 && (
            <div className="mb-2">
              <h2 className="font-display text-2xl font-bold text-slate-900">Find Your Points</h2>
              <p className="mt-1 text-xs text-slate-600">Copy each choice from Section 1 by ticking the same rating below. The number after each rating is its points.</p>
            </div>
          )}
          {qs.map((x, i) => <Question key={x.key} n={starts[p] + i} q={x} desc={desc[x.key]} />)}
        </div>
      ),
    })),
    {
      section: "score",
      el: (
        <div className="pk-fit" data-testid="pk-scoring">
          <h2 className="font-display text-2xl font-bold text-slate-900">Score Yourself</h2>
          <p className="mt-1 text-xs text-slate-600">Write each topic's points in the Points column, then copy the same number into every white box on that row. Add each column for your area totals, then use the key to find your level.</p>
          <ScoringTable questions={questions} />
          <ScoringKeys />
        </div>
      ),
    },
    { section: "color", el: <SectionCover id="color" /> },
    { section: "color", el: <div className="pk-fit pk-fit-tight"><ColoringSheetContent /></div> },
    { section: "grow", el: <SectionCover id="grow" /> },
    ...growPages(),
  ];

  return (
    <div className="mx-auto max-w-[8.5in]" data-testid="workshop-pack">
      <PrintBar title="Workshop Pack · booklet (print double-sided)" />
      <PackDocument pages={pages} testId="workshop-pack-pages" />
    </div>
  );
}

export function GrowthWorkbook() {
  usePackPrint();
  const pages = [
    { section: null, el: <PackCover title="Grow & Track Workbook" subtitle="Growth plans · daily tracker · next step planner" />, plain: true },
    { section: "grow", el: <SectionCover id="grow" /> },
    ...growPages(),
  ];
  return (
    <div className="mx-auto max-w-[8.5in]" data-testid="growth-workbook">
      <PrintBar title="Grow & Track Workbook (paper version)" />
      <PackDocument pages={pages} testId="growth-workbook-pages" />
    </div>
  );
}
